import React from 'react';

import {
  Box,
  HStack,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { IconBadge } from '@saas-ui/react';
import { Package } from 'lucide-react';

import { Card, CardHeader, CardTitle } from '@/components/core';
import { PartsCatalogHit } from '@/types/quoteDocuments';
import { partsCatalogHitSublabel } from '@/utils/partsCatalogHit';

const SKELETON_COUNT = 6;

interface PartsPreviewSectionProps {
  hits: PartsCatalogHit[];
  isLoading?: boolean;
}

export const PartsPreviewSection: React.FC<PartsPreviewSectionProps> = ({
  hits,
  isLoading = false,
}) => {
  if (!isLoading && hits.length === 0) {
    return null;
  }

  return (
    <VStack align='stretch' gap={4} w='full' data-cy='global-search-parts'>
      <Stack gap={0} align='stretch'>
        <Text
          fontSize='md'
          fontWeight='semibold'
          color='tabs.font-selected'
          borderBottom='2px solid'
          borderColor='tabs.font-selected'
          w='fit-content'
          pb={2}
          mb={2}
        >
          Parts
        </Text>
        <SimpleGrid
          columns={{ base: 1, xl: 3 }}
          spacing={{ base: 2, xl: 2.5 }}
          w='full'
        >
          {isLoading && hits.length === 0
            ? Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                <Skeleton
                  key={`parts-preview-skeleton-${index}`}
                  height='72px'
                  rounded='2xl'
                  startColor='glass-items.bg-active'
                  endColor='cards.bg'
                />
              ))
            : hits.map(hit => (
                <PartsPreviewCard key={hit.id} hit={hit} />
              ))}
        </SimpleGrid>
      </Stack>
    </VStack>
  );
};

interface PartsPreviewCardProps {
  hit: PartsCatalogHit;
}

const PartsPreviewCard: React.FC<PartsPreviewCardProps> = ({ hit }) => {
  const sublabel = partsCatalogHitSublabel(hit);

  return (
    <Card
      px={0}
      py={4}
      rounded='2xl'
      color='white'
      cursor='default'
      bg='cards.bg'
      border='1px solid'
      borderColor='border.light'
      overflow='hidden'
      data-cy='global-search-part'
      data-catalog-id={hit.id}
    >
      <CardHeader py={0} px={4}>
        <HStack align='flex-start' gap={2.5} w='full' minW={0}>
          <Box flexShrink={0}>
            <IconBadge
              icon={<Package width='16px' height='16px' />}
              variant='solid'
              colorScheme='whiteAlpha'
              isRound
              boxSize='36px'
              backgroundColor='glass-items.bg-active'
            />
          </Box>
          <Stack flex={1} gap={0} minW={0} maxW='100%'>
            <CardTitle
              fontSize='sm'
              lineHeight='5'
              fontWeight='normal'
              noOfLines={1}
            >
              {hit.title || 'Untitled part'}
            </CardTitle>
            {sublabel ? (
              <Text
                fontSize='xs'
                lineHeight='4'
                opacity={0.5}
                noOfLines={{ base: 2, xl: 1 }}
                color='fonts.primary'
              >
                {sublabel}
              </Text>
            ) : null}
          </Stack>
        </HStack>
      </CardHeader>
    </Card>
  );
};
